'use client';
import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, Activity, Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SessionDefinition {
  name: string;
  openHour: number;
  openMinute: number;
  closeHour: number;
  closeMinute: number;
}

type SessionStatus = 'active' | 'inactive' | 'overlap';

interface SessionInfo extends SessionDefinition {
  status: SessionStatus;
  color: string;
  bgColor: string;
  borderColor: string;
}

const SESSION_DEFINITIONS: SessionDefinition[] = [
  { name: 'Sydney',   openHour: 22, openMinute: 0, closeHour: 7,  closeMinute: 0 },
  { name: 'Tokyo',    openHour: 0,  openMinute: 0, closeHour: 9,  closeMinute: 0 },
  { name: 'London',   openHour: 8,  openMinute: 0, closeHour: 17, closeMinute: 0 },
  { name: 'New York', openHour: 13, openMinute: 0, closeHour: 22, closeMinute: 0 },
];

function isSessionOpen(def: SessionDefinition, utcMinutes: number): boolean {
  const open  = def.openHour  * 60 + def.openMinute;
  const close = def.closeHour * 60 + def.closeMinute;
  if (open < close) {
    // Normal range (e.g. 08:00–17:00)
    return utcMinutes >= open && utcMinutes < close;
  } else {
    // Overnight range (e.g. 22:00–07:00 crosses midnight)
    return utcMinutes >= open || utcMinutes < close;
  }
}

function computeSessions(now: Date): SessionInfo[] {
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const openStates = SESSION_DEFINITIONS.map((def) => isSessionOpen(def, utcMinutes));

  return SESSION_DEFINITIONS.map((def, i) => {
    const isOpen = openStates[i];

    // Overlap: this session is open AND at least one other session is also open
    const hasOverlap = isOpen && openStates.some((s, j) => j !== i && s);
    const status: SessionStatus = !isOpen ? 'inactive' : hasOverlap ? 'overlap' : 'active';

    let color: string;
    let bgColor: string;
    let borderColor: string;

    if (status === 'inactive') {
      color = 'text-zinc-500';
      bgColor = 'bg-zinc-800/40';
      borderColor = 'border-zinc-700/30';
    } else if (status === 'overlap') {
      color = 'text-amber-400';
      bgColor = 'bg-amber-500/10';
      borderColor = 'border-amber-500/20';
    } else {
      color = 'text-blue-400';
      bgColor = 'bg-blue-500/10';
      borderColor = 'border-blue-500/20';
    }

    return { ...def, status, color, bgColor, borderColor };
  });
}

interface MarketInfo {
  bestWindow: string;
  volatility: 'HIGH' | 'MEDIUM' | 'LOW';
  volatilityColor: string;
}

function computeMarketInfo(sessions: SessionInfo[]): MarketInfo {
  const byName = Object.fromEntries(sessions.map((s) => [s.name, s.status]));
  const londonOpen   = byName['London']   !== 'inactive';
  const nyOpen       = byName['New York'] !== 'inactive';
  const tokyoOpen    = byName['Tokyo']    !== 'inactive';
  const sydneyOpen   = byName['Sydney']   !== 'inactive';

  // Best window label
  let bestWindow: string;
  if (londonOpen && nyOpen) {
    bestWindow = '13:00–17:00 UTC'; // London + NY overlap
  } else if (londonOpen) {
    bestWindow = '08:00–13:00 UTC'; // London only
  } else if (nyOpen) {
    bestWindow = '13:00–22:00 UTC'; // NY only
  } else if (tokyoOpen && sydneyOpen) {
    bestWindow = '00:00–07:00 UTC'; // Tokyo + Sydney overlap
  } else if (tokyoOpen) {
    bestWindow = '00:00–09:00 UTC'; // Tokyo only
  } else if (sydneyOpen) {
    bestWindow = '22:00–07:00 UTC'; // Sydney only
  } else {
    bestWindow = '08:00–17:00 UTC'; // Next: London open
  }

  // Volatility
  let volatility: 'HIGH' | 'MEDIUM' | 'LOW';
  let volatilityColor: string;
  if (londonOpen && nyOpen) {
    volatility = 'HIGH';
    volatilityColor = 'text-emerald-400';
  } else if (londonOpen || nyOpen) {
    volatility = 'MEDIUM';
    volatilityColor = 'text-amber-400';
  } else {
    volatility = 'LOW';
    volatilityColor = 'text-zinc-500';
  }

  return { bestWindow, volatility, volatilityColor };
}

export default function SessionStatusBar() {
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [sessions, setSessions] = useState<SessionInfo[]>(() => computeSessions(new Date()));
  const [marketInfo, setMarketInfo] = useState<MarketInfo>(() => computeMarketInfo(computeSessions(new Date())));
  const { t } = useLanguage();

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const updatedSessions = computeSessions(now);
      setSessions(updatedSessions);
      setMarketInfo(computeMarketInfo(updatedSessions));
      setCurrentTime(
        now.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'UTC',
        })
      );
      setCurrentDate(
        now.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full border-b border-white/5 bg-zinc-950/60">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between gap-2 py-2 sm:py-3 overflow-x-auto scrollbar-hide">
          {/* Sessions */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
            <Globe className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-zinc-600 flex-shrink-0" />
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              {sessions.map((session) => (
                <div
                  key={`session-${session.name}`}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md border text-[10px] sm:text-[11px] font-mono font-medium ${session.bgColor} ${session.borderColor} ${session.color} flex-shrink-0`}
                >
                  {session.status !== 'inactive' && (
                    <div
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        session.status === 'overlap' ? 'bg-amber-400 pulse-gold' : 'bg-blue-400 pulse-gold'
                      }`}
                    />
                  )}
                  <span className="hidden xs:inline sm:inline">{session.name}</span>
                  <span className="xs:hidden sm:hidden">{session.name.slice(0, 3)}</span>
                  {session.status === 'overlap' && (
                    <span className="text-amber-400/70 text-[9px] hidden sm:inline">{t('overlap')}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Center info */}
          <div className="hidden lg:flex items-center gap-5 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3 text-amber-400/70" />
              <span className="text-[11px] font-mono text-zinc-500">{t('bestWindow')}</span>
              <span className="text-[11px] font-mono text-amber-400 font-medium">{marketInfo.bestWindow}</span>
            </div>
            <div className="w-px h-3 bg-zinc-800" />
            <div className="flex items-center gap-1.5">
              <Activity className={`w-3 h-3 ${marketInfo.volatilityColor} opacity-70`} />
              <span className="text-[11px] font-mono text-zinc-500">{t('volatility')}</span>
              <span className={`text-[11px] font-mono font-medium ${marketInfo.volatilityColor}`}>{marketInfo.volatility}</span>
            </div>
          </div>

          {/* UTC Clock */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-zinc-600" />
            <div className="flex flex-col items-end">
              <span className="text-[11px] sm:text-xs font-mono text-zinc-300 font-medium tabular-nums leading-none">
                {currentTime}
              </span>
              <span className="text-[9px] sm:text-[10px] font-mono text-zinc-600 leading-none mt-0.5 sm:mt-1">
                {currentDate} UTC
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}